// Cio che e DELLA SCALA 40 nel tavolo: il dominio delle rotte del BFF, i posti, come si compone l'azione, le frasi dei rifiuti, come si disegna una carta. Il trasporto (socket, ticket, REST, seed, hook) e
// `lib/gamehost/`: qui se ne passano i PARAMETRI e basta (ADR-0235 §4). Stessa forma di `lib/ginrummy/gioco.ts` (TMA#748), senza copiarne il codice: i file del gin non si copiano a blocchi (ADR-0235).
import type { PostoPubblico } from '@/lib/gamehost/protocol';
import { creaTavoloBody } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import type { Mossa, MossaDiPersona } from './protocol';

/** Il segmento di `/v1/games/<dominio>` (`limulo-app/gioco.js` di QScala40: `dominio: 'scala40'`). */
export const SCALA40_DOMINIO = 'scala40';

/** I posti che il gioco ammette (`POSTI_AMMESSI` di `apps/qscala40-gameserver/src/regole.ts`): da 2 a 6 (I-922, L-3454 b). */
export const POSTI_AMMESSI: readonly number[] = [2, 3, 4, 5, 6];
export const POSTI_MIN = 2;
export const POSTI_MAX = 6;
/** Il formato che «Gioca» propone per primo. */
export const POSTI_PREDEFINITI = 2;

/** I tre livelli del robot (contratto I-833, come il «Contro i robot» della briscola): il nome visibile e `livello.<L>` nel catalogo. */
export const LIVELLI_ROBOT = ['L0', 'L1', 'L2'] as const;
export type LivelloRobot = (typeof LIVELLI_ROBOT)[number];

/**
 * La richiesta di un tavolo nuovo. UN solo posto dove si compone il corpo (`creaTavoloScala40Body`). Un tavolo e di persone, `casual` (chi lo completa lo decide il game host) o `privato` (su invito); col
 * `livello` e un tavolo «contro il robot»: SEMPRE `casual` (un invito non ha senso: nessun posto da riempire) e mai classificato.
 */
export type RichiestaTavoloScala40 = { posti: number; idempotencyKey: string } & ({ tipo: 'casual' | 'privato'; livello?: undefined } | { tipo: 'casual'; livello: LivelloRobot });

/**
 * Il corpo di un tavolo nuovo: `tipo`, `posti` (2..6) e la chiave di idempotenza e, SOLO se si sceglie un livello, `robot: { livello }` e nient'altro (il BFF rifiuta ogni altra forma: `INVALID_ROBOT`). Col campo
 * `robot` chi crea siede a un posto estratto dal core e OGNI altro posto e un robot del pool dello stesso livello (un account per posto). Mai `classificato`: ranked e tornei sono P9, solo a 2 posti e senza robot (ADR-0212, ADR-0235).
 */
export function creaTavoloScala40Body(a: RichiestaTavoloScala40) {
  const base = creaTavoloBody({ tipo: a.tipo, posti: a.posti, idempotencyKey: a.idempotencyKey });
  return a.livello === undefined ? base : { ...base, robot: { livello: a.livello } };
}

/**
 * Il 503 di «Contro il robot»: il core risponde `503 no_robot_available` quando il pool del gioco non ha abbastanza account liberi, e nessun tavolo nasce. Si riconosce dallo status E dal codice (ovunque stia nel
 * corpo dell'errore): un ALTRO 503 (il servizio che non risponde) resta un guasto generico.
 */
export function eRobotNonDisponibili(errore: unknown): boolean {
  const e = errore as { status?: unknown; data?: unknown } | null;
  if (e === null || typeof e !== 'object' || e.status !== 503) return false;
  try {
    return JSON.stringify(e.data ?? null).includes('no_robot_available');
  } catch {
    return false;
  }
}

/**
 * L'azione che parte sul filo: la mossa scelta con il posto aggiunto, e NIENT'ALTRO. `puòAgire` del game host la ricostruisce campo per campo (`leggiMossa`: una chiave in piu e `ILLEGALE`) e la fa validare
 * da `applica`. Funzione di MODULO: vale prima e dopo che `useTavoloGamehost` tenga l'ultima callback in un ref.
 */
export const componiAzione = (posto: number, da: MossaDiPersona): Mossa => ({ ...da, posto }) as Mossa;

/** Il prefisso che il game host mette all'etichetta di un robot (kit, `ETICHETTA_BOT`, ADR-0212 §2.1: «🤖 Bot»). La TMA disegna gia il suo `BadgeBot`: l'icona sta una volta sola. */
const PREFISSO_ROBOT = '🤖 ';

/** L'etichetta di un posto come la zona la stampa: per un posto ROBOT senza il «🤖 » iniziale (c'e il `BadgeBot`); per una PERSONA, anche col nickname che comincia cosi, e per un robot senza prefisso, invariata. */
export function etichettaDelPosto(posto: Pick<PostoPubblico, 'tipo' | 'etichetta'>): string {
  return posto.tipo === 'robot' && posto.etichetta.startsWith(PREFISSO_ROBOT) ? posto.etichetta.slice(PREFISSO_ROBOT.length) : posto.etichetta;
}

/** Il nome di un posto: il nickname di chi siede; un posto libero (o senza nome) e `libero`, la frase che passa chi chiama. Mai un ripiego su email o nome anagrafico (ADR-0238, I-898). */
export function nomeDelPosto(posti: readonly PostoPubblico[], p: number, libero: string): string {
  const pp = posti.find((x) => x.posto === p);
  return pp && pp.tipo !== 'libero' && pp.etichetta.trim() !== '' ? etichettaDelPosto(pp) : libero;
}

export const eRobot = (posti: readonly PostoPubblico[], p: number): boolean => posti.find((x) => x.posto === p)?.tipo === 'robot';

/**
 * Le frasi dei rifiuti (`lib/gamehost/rifiuti.ts`). Il game host della scala 40 non manda MAI un `reason`: ogni rifiuto ha il suo `code` (quelli di `puòAgire` e di `applica`), e dal core vengono STALE_SEQ e
 * THROTTLED. Un codice che non e qui ha la frase generica. L'elenco e CHIUSO: `tests/scala40-regole.test.ts` pretende che ogni codice che `provaMossa` puo dare ci sia.
 */
export const VOCI_RIFIUTO: VociRifiuto = {
  motivi: [],
  codici: [
    'FUORI_TURNO',
    'FASE_ERRATA',
    'NON_TUO',
    'ILLEGALE',
    'STALE_SEQ',
    'FINITA',
    'NON_DISTRIBUITO',
    'THROTTLED',
    'GIA_ELIMINATO',
    'TALLONE_VUOTO',
    'POZZO_VUOTO',
    'NON_APERTO',
    'GIA_APERTO',
    'GIOCO_NON_VALIDO',
    'GIOCO_INESISTENTE',
    'CARTA_NON_USATA',
    'CARTA_NON_IN_MANO',
    'APERTURA_BASSA',
    'RESTO_NON_AMMESSO',
    'NON_SOSTITUISCE',
    'NON_ATTACCA',
    'CHIUSURA_PRIMO_GIRO',
    'SCARTO_JOLLY',
    'SCARTO_NON_LEGALE',
    'MANO_NON_FINITA',
  ],
};
